import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';
import { ScootFilters as Filters } from '../../hooks/useScootQuery';

interface ScootFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

export default function ScootFilters({ filters, onFiltersChange }: ScootFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleReset = () => {
    onFiltersChange({
      minFollowers: undefined,
      maxFollowers: undefined,
      minEngagement: undefined,
      maxEngagement: undefined,
      minAvgViews: undefined,
      maxAvgViews: undefined,
    });
  };

  const hasActiveFilters =
    filters.minFollowers !== undefined ||
    filters.maxFollowers !== undefined ||
    filters.minEngagement !== undefined ||
    filters.maxEngagement !== undefined ||
    filters.minAvgViews !== undefined ||
    filters.maxAvgViews !== undefined;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-medium transition-all ${
          hasActiveFilters
            ? 'bg-white/[0.10] border-white/20 text-white'
            : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
        }`}
      >
        <Filter className="w-4 h-4" />
        <span>Filtres</span>
        {hasActiveFilters && (
          <span className="bg-white text-black text-xs font-bold px-2 py-0.5 rounded-full">
            {[
              filters.minFollowers,
              filters.maxFollowers,
              filters.minEngagement,
              filters.maxEngagement,
              filters.minAvgViews,
              filters.maxAvgViews,
            ].filter((v) => v !== undefined).length}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full mt-2 right-0 z-50 bg-[#141414] rounded-2xl border border-white/[0.08] shadow-2xl p-5 w-80">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Filtres</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/40 hover:text-white/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Nombre d'abonnés
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Minimum</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={filters.minFollowers || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        onFiltersChange({ ...filters, minFollowers: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Maximum</label>
                    <input
                      type="number"
                      placeholder="∞"
                      value={filters.maxFollowers || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        onFiltersChange({ ...filters, maxFollowers: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { label: '1K-10K', min: 1000, max: 10000 },
                    { label: '10K-50K', min: 10000, max: 50000 },
                    { label: '50K-100K', min: 50000, max: 100000 },
                    { label: '100K+', min: 100000, max: undefined },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          minFollowers: preset.min,
                          maxFollowers: preset.max,
                        })
                      }
                      className="px-3 py-1 text-xs font-medium bg-white/[0.06] hover:bg-white/[0.10] text-white/50 hover:text-white/80 rounded-lg transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Taux d'engagement (%)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Minimum</label>
                    <input
                      type="number"
                      placeholder="0"
                      step="0.1"
                      value={filters.minEngagement || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        onFiltersChange({ ...filters, minEngagement: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Maximum</label>
                    <input
                      type="number"
                      placeholder="∞"
                      step="0.1"
                      value={filters.maxEngagement || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                        onFiltersChange({ ...filters, maxEngagement: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { label: '< 2%', min: undefined, max: 2 },
                    { label: '2-5%', min: 2, max: 5 },
                    { label: '5-10%', min: 5, max: 10 },
                    { label: '> 10%', min: 10, max: undefined },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          minEngagement: preset.min,
                          maxEngagement: preset.max,
                        })
                      }
                      className="px-3 py-1 text-xs font-medium bg-white/[0.06] hover:bg-white/[0.10] text-white/50 hover:text-white/80 rounded-lg transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Vues moyennes de reels
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Minimum</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={filters.minAvgViews || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        onFiltersChange({ ...filters, minAvgViews: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">Maximum</label>
                    <input
                      type="number"
                      placeholder="∞"
                      value={filters.maxAvgViews || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : undefined;
                        onFiltersChange({ ...filters, maxAvgViews: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { label: '< 10K', min: undefined, max: 10000 },
                    { label: '10K-50K', min: 10000, max: 50000 },
                    { label: '50K-100K', min: 50000, max: 100000 },
                    { label: '100K+', min: 100000, max: undefined },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          minAvgViews: preset.min,
                          maxAvgViews: preset.max,
                        })
                      }
                      className="px-3 py-1 text-xs font-medium bg-white/[0.06] hover:bg-white/[0.10] text-white/50 hover:text-white/80 rounded-lg transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-5 pt-4 border-t border-white/[0.06]">
              <button
                onClick={handleReset}
                disabled={!hasActiveFilters}
                className="flex-1 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-40 disabled:cursor-not-allowed text-white/60 font-medium rounded-xl transition-colors"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 px-4 py-2.5 bg-white text-black hover:bg-white/90 font-semibold rounded-xl transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
